/* eslint-disable import/no-extraneous-dependencies */
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import React, { FC, memo, useCallback } from 'react';
import dotsGrid06 from 'src/assets/icons/dots-grid-06.svg';
import eyeOff from 'src/assets/icons/filled-icons/eye-off.svg';
import eye from 'src/assets/icons/filled-icons/eye.svg';
import TooltipCmp from 'src/components/TooltipCmp';

import { IDraggableColumnProps } from './draggableLayout.interface';

const DraggableColumn: FC<IDraggableColumnProps> = ({ column, sortableData, renderCellContent: CellRenderer, onColumnVisibilityToggle }) => {
    const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: column.id });

    const style = {
        transform: CSS.Translate.toString(transform),
        transition,
        zIndex: isDragging ? 10 : 'auto',
        position: isDragging ? 'relative' : undefined,
    } as React.CSSProperties;

    const handleEyeClick = useCallback(
        (e: React.MouseEvent) => {
            e.stopPropagation();
            onColumnVisibilityToggle(column.id);
        },
        [column.id, onColumnVisibilityToggle]
    );

    const handleEyePointerDown = useCallback((e: React.PointerEvent) => {
        e.stopPropagation();
    }, []);

    const visible = column.visible !== undefined ? column.visible : true;

    return (
        <div
            ref={setNodeRef}
            style={style}
            className={`rounded-lg border bordr-utilityGray200 shadow-sm w-fit flex flex-col bg-white no-selection-highlight cursor-grab ${column?.boxWrapClassName} ${!visible && 'opacity-50'} `}
            {...attributes}
            {...listeners}
        >
            {/* Header Cell */}
            <div className="rounded-t-[inherit] px-3 py-[9px] border-b border-utilityGray200 bg-gray50 flex items-center gap-x-1.5 min-w-[148px]">
                <h6 className="truncate text-grayLight600 text-xs leading-[1.5] font-medium flex-1">{column.name}</h6>
                <TooltipCmp message={visible ? 'Hide column' : 'Show column'} position="top">
                    <figure className="flex-shrink-0 cursor-pointer" onClick={handleEyeClick} onPointerDown={handleEyePointerDown}>
                        <img src={visible ? eye : eyeOff} className="w-4 h-4" alt={visible ? 'visible' : 'hidden'} />
                    </figure>
                </TooltipCmp>
                <figure className="flex-shrink-0">
                    <img src={dotsGrid06} className="w-4 h-4" alt="dots-grid" />
                </figure>
            </div>

            {/* Body Cells for this column */}
            {sortableData.map((row: any, rowIndex: number) => {
                const cellData = row.find((item: any) => item.id === column.id);

                return (
                    <div key={`${rowIndex}-${column.id}`} className="px-3 py-[11px] text-grayLight600 font-normal text-xs bg-transparent flex-1 flex flex-col justify-center items-start">
                        {cellData ? <CellRenderer columnName={column.name} data={cellData.data} /> : '-'}
                    </div>
                );
            })}
        </div>
    );
};

export default memo(DraggableColumn);
